import PaymentForm from "./PaymentForm.js";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { Box, Divider, Typography, Link, Icon } from "@mui/material";
import { useTheme } from "@emotion/react";
import { getPageRoute } from "src/services/Config.js";
import CountdownTimer from "src/components/blocks/CountdownTimer.js";

const STEP_TRANSITION = 300;

const Step = ({ position, title, children, visible }) => {
  const theme = useTheme();

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            width: "30px",
            height: "30px",
            border: `1px solid ${
              visible
                ? theme.palette.border.secondary
                : theme.palette.border.tertiary
            }`,
            fontWeight: "700",
            borderRadius: "100%",
            placeContent: "center",
            color: visible
              ? theme.palette.text.primary
              : theme.palette.text.tertiary,
          }}
        >
          {position}
        </Box>
        <Typography
          variant={"h5"}
          sx={{
            color: visible
              ? theme.palette.text.primary
              : theme.palette.text.tertiary,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          maxHeight: visible ? "999px" : "0px",
          transition: `all ease ${STEP_TRANSITION}ms`,
          opacity: visible ? "1" : "0",
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        {children}
      </Box>
      <Divider
        sx={{
          transition: `all ease ${STEP_TRANSITION}ms`,
          mt: visible ? 3 : 1,
          mb: 3,
        }}
      />
    </>
  );
};

const SelectPlan = ({ options, planTypeSelected, setPlanTypeSelected }) => {
  const optionsShown = planTypeSelected
    ? options.filter((option) => option.id === planTypeSelected)
    : options;
  const theme = useTheme();

  const Option = ({
    id,
    title,
    subtitle,
    originalPrice,
    label,
    special = false,
    isSelected,
    onSelect,
  }) => (
    <Box
      sx={{
        display: "flex",
        border: `${isSelected ? "1.5px" : "1px"} solid ${
          isSelected
            ? theme.palette.border.primary
            : theme.palette.border.secondary
        }`,
        justifyContent: "space-between",
        alignItems: "center",
        p: 1.25,
        cursor: "pointer",
        minHeight: "28px", // Ensure consistent height
      }}
      onClick={() => onSelect(id)}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
        <Box
          sx={{
            width: "20px",
            height: "20px",
            border: `1px solid ${
              isSelected
                ? theme.palette.text.primary
                : theme.palette.text.tertiary
            }`,
            borderRadius: "100%",
            backgroundColor: isSelected
              ? theme.palette.text.primary
              : "transparent",
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {originalPrice && (
              <Typography 
                variant={"h5"}
                sx={{ 
                  textDecoration: "line-through", 
                  color: "text.secondary"
                }}
              >
                {originalPrice}
              </Typography>
            )}
            <Typography variant={"h5"}>
              {title}
            </Typography>
          </Box>
          {subtitle && (
            <Typography 
              variant={"subtitle2"}
              sx={{ 
                color: "text.secondary",
                mt: 0.25,
                alignSelf: "flex-start"
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {special && (
        <Typography
          variant={"subtitle1"}
          sx={{
            py: "5px",
            px: "11px",
            borderRadius: "100px",
            backgroundColor: "black",
            color: "white",
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {optionsShown.map((option) => (
          <Option
            key={option.id}
            {...option}
            isSelected={option.id === planTypeSelected}
            onSelect={(id) => setPlanTypeSelected(id)}
          />
        ))}
      </Box>
      {planTypeSelected && (
        <Link
          as="div"
          sx={{
            cursor: "pointer",
            textAlign: "left",
            fontSize: "14px",
            textDecorationColor: theme.palette.text.primary,
            mt: 1.25,
          }}
          onClick={() => setPlanTypeSelected(null)}
        >
          Change Selection
        </Link>
      )}
    </>
  );
};

const DEFAULT_SUCCESS_URL = getPageRoute(
  "upgrade",
  "PAYMENT_STANZA_PLUS_CONFIRMATION"
);

const NativeCheckoutWithSteps = ({ trialPeriodDays = 7, successUrl = DEFAULT_SUCCESS_URL }) => {
  const user = useContext(AuthContext).user;
  const theme = useTheme();

  const [planTypeSelected, setPlanTypeSelected] = useState(null);
  const [planTypeAfterTransition, setPlanTypeAfterTransitione] =
    useState(planTypeSelected);

  useEffect(() => {
    setTimeout(
      () => {
        setPlanTypeAfterTransitione(planTypeSelected);
      },
      planTypeSelected === null ? STEP_TRANSITION : 0
    );
  }, [planTypeSelected]);

  const planOptions = [
    {
      id: "consumer_annual",
      title: "$2.50 / month",
      subtitle: "Billed annually",
      originalPrice: "$5.00",
      label: "Best Value",
      special: true,
    },
    {
      id: "consumer_monthly",
      title: "$5 / month",
      subtitle: "Billed every month",
    },
  ];

  return user ? (
    <>
      {/* Header */}
      <Box
        sx={{
          margin: "0 auto 2rem auto",
          maxWidth: { xs: "100%", sm: "400px" },
          textAlign: "center",
        }}
      >
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            margin: "24px auto",
            color: `${theme.palette.icon.primary}`,
            verticalAlign: "middle",
            fontSize: "3rem",
            mt: 0,
            mb: 1,
            fontWeight: 300,
          }}
        >
          payment
        </Icon>
        <Typography variant="title" sx={{ mb: 1 }}>
          Stanza+
        </Typography>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {trialPeriodDays} free days
        </Typography>
        <CountdownTimer label="TRIAL OFFER ENDS SOON:" />
      </Box>

      <Step position="1" title="Choose your plan:" visible={true}>
        <SelectPlan
          options={planOptions}
          planTypeSelected={planTypeSelected}
          setPlanTypeSelected={setPlanTypeSelected}
        />
      </Step>
      <Step position="2" title="Checkout" visible={Boolean(planTypeSelected)}>
        <PaymentForm
          type={planTypeAfterTransition}
          trialPeriodDays={trialPeriodDays}
          successUrl={successUrl}
        />
      </Step>
    </>
  ) : (
    <></>
  );
};

export default NativeCheckoutWithSteps;
